import { Dtos } from "@inrev/common";
import { useFetchFollowedEntities } from "@portal/api/followers";
import {
	useFetchAccountPreviews,
	useFetchBondPreviews,
	useFetchBondRequestPreviews,
} from "@portal/api/previews";
import {
	formatMetricCardCurrency,
	TrendMetricCard,
	type TrendMetricCardProps,
} from "@portal/components/patterns/metric-card";
import { Spinner } from "@portal/components/primitives";
import { useMemo } from "react";
import type { EntityScope } from "../../../shared/scope";
import { computeAgentMetrics } from "./computeAgentMetrics";

const EMPTY_ACCOUNT_PREVIEWS: Dtos.SuretyAccount.Preview.Get.Response[] = [];
const EMPTY_BOND_PREVIEWS: Dtos.SuretyBond.Preview.Get.Response[] = [];
const EMPTY_BOND_REQUEST_PREVIEWS: Dtos.SuretyQuote.Preview.Get.Response[] = [];

type AgentDashboardMetricsViewProps = {
	scope: EntityScope;
};

export const AgentDashboardMetricsView = ({ scope }: AgentDashboardMetricsViewProps) => {
	const { accountPreviews, accountPreviewsIsLoading } = useFetchAccountPreviews();
	const { bondPreviews, bondPreviewsIsLoading } = useFetchBondPreviews();
	const { bondRequestPreviews, bondRequestPreviewsIsLoading } = useFetchBondRequestPreviews();
	const { followed, followedEntitiesIsLoading } = useFetchFollowedEntities({
		enabled: scope === "me",
	});

	const isLoading =
		accountPreviewsIsLoading ||
		bondPreviewsIsLoading ||
		bondRequestPreviewsIsLoading ||
		(scope === "me" && followedEntitiesIsLoading);

	const metrics = useMemo(
		() =>
			computeAgentMetrics({
				scope,
				followed,
				accountPreviews: accountPreviews ?? EMPTY_ACCOUNT_PREVIEWS,
				bondPreviews: bondPreviews ?? EMPTY_BOND_PREVIEWS,
				bondRequestPreviews: bondRequestPreviews ?? EMPTY_BOND_REQUEST_PREVIEWS,
			}),
		[scope, followed, accountPreviews, bondPreviews, bondRequestPreviews],
	);

	if (isLoading) {
		return (
			<div data-testid="agent-dashboard-metrics-loading" className="flex justify-center py-10">
				<Spinner />
			</div>
		);
	}

	const trailing90DayGwpCurrency = formatMetricCardCurrency(
		metrics.trends.trailing90DayGwp.current,
	);
	const trailing90DayCommissionCurrency = formatMetricCardCurrency(
		metrics.trends.trailing90DayCommission.current,
	);

	const cards: TrendMetricCardProps[] = [
		{
			testId: "metric-open-submissions",
			label: "Open Submissions",
			value: metrics.openSubmissions,
			tone: "accent",
		},
		{
			testId: "metric-open-bond-requests",
			label: "Open Bond Requests",
			value: metrics.openBondRequests,
			tone: "accent",
		},
		{
			testId: "metric-active-accounts",
			label: "Active Accounts",
			value: metrics.activeAccounts,
			tone: "accent",
		},
		{
			testId: "metric-trailing-90-day-gwp",
			label: "Trailing 90d GWP",
			value: trailing90DayGwpCurrency.display,
			valueTooltip: trailing90DayGwpCurrency.full,
			deltaFormat: "percent",
			tone: "confirm",
			...metrics.trends.trailing90DayGwp,
		},
		{
			testId: "metric-trailing-90-day-commission",
			label: "Trailing 90d Commission",
			value: trailing90DayCommissionCurrency.display,
			valueTooltip: trailing90DayCommissionCurrency.full,
			deltaFormat: "percent",
			tone: "confirm",
			...metrics.trends.trailing90DayCommission,
		},
	];

	return (
		<div className="grid max-w-8xl grid-cols-3 gap-4 lg:grid-cols-5">
			{cards.map((card) => (
				<TrendMetricCard key={card.testId} {...card} />
			))}
		</div>
	);
};
